import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateFnsLocale } from '../../hooks/useDateFnsLocale';
import { format, isToday, isPast, isTomorrow } from 'date-fns';
import { CheckCircle2, Circle, Clock, Trash2, Lock, Repeat, AlertTriangle, Eye, Crown, UserCheck, Calendar, Pencil, ChevronDown, ChevronUp, FileText, StickyNote, Tag, Users, FolderOpen, Bell, UserPlus, Image as ImageIcon } from 'lucide-react';
import type { Task } from '../../types';
import { TaskGroupCopiesDropdown } from './TaskGroupCopiesDropdown';
import { ImageLightbox } from './ImageLightbox';
import { useTaskStore } from '../../stores/taskStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { useProjectStore } from '../../stores/projectStore';
import { useRelativeTime } from '../../hooks';
import { Button, Modal, showToast } from '../common';
import { attachmentApi } from '../../services/api';
import type { Attachment } from '../../types';
import { canClaimTask } from '../../utils/canClaimTask';

// Helper to determine task relationship to current user
interface TaskRelationship {
  isCreatedByMe: boolean;
  isAssignedToMe: boolean;
}

const getTaskRelationship = (task: Task, userId: string | undefined): TaskRelationship => {
  return {
    isCreatedByMe: task.creatorId === userId,
    isAssignedToMe: task.assigneeId === userId,
  };
};

interface TaskCardProps {
  task: Task;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task }) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  const { toggleComplete, deleteTask, expandedTaskId, toggleExpand, assignToMe, groupCopies } = useTaskStore();
  const { openTaskModal } = useUIStore();
  const { user } = useAuthStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Determine if this specific card is expanded
  const isExpanded = expandedTaskId === task.id;

  // Relative time display for task creation
  const { relativeTime: createdAgo } = useRelativeTime(task.createdAt);

  // Permission logic: org admin can do anything within their org
  const isOrgAdmin = user?.organizationRoles?.find(r => r.orgId === user.activeOrganizationId)?.role === 'ADMIN';

  // Get project membership for permission check
  const projects = useProjectStore((state) => state.projects);
  const project = projects.find((p) => p.id === task.projectId);
  const membership = project?.members?.find((m) => m.userId === user?.id);
  const projectRole = membership?.role;

  // Permission logic for edit/delete:
  // - Org admin: can edit/delete any task in their org
  // - Project ADMIN: can edit/delete any task in their project
  // - Creator: ALWAYS can edit/delete their own tasks (even without project)
  // - VIEWER or non-member non-creator: cannot edit/delete
  const isCreator = task.creatorId === user?.id;
  // ISSUE 4 FIX: Project owner is also an admin, even if not in members list
  const isProjectOwner = project?.ownerId === user?.id;
  const canEdit = isOrgAdmin || projectRole === 'ADMIN' || isProjectOwner || isCreator;
  const canDelete = isOrgAdmin || projectRole === 'ADMIN' || isProjectOwner || isCreator;
  
  // Check if user can claim this task via 'Asignarme' button
  // Uses shared helper that mirrors backend permission logic (see utils/canClaimTask.ts)
  const canClaimTaskResult = canClaimTask(task, user, project);

  // Individual task copies: creator sees a dropdown with sibling copies
  // Only applies when: task has taskGroupId, current user is the creator, and task is NOT a group task
  const isCreatorAndHasGroup = !!task.taskGroupId && task.creatorId === user?.id && !task.isGroupTask;

  const handleAssignToMe = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const success = await assignToMe(task.id);
      if (!success) {
        showToast(t('tasks.error.failedToAssign'), 'error');
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        showToast(t('tasks.card.someoneElseClaimed'), 'error');
      } else {
        showToast(t('tasks.error.failedToAssign'), 'error');
      }
    }
  };
  
  // Completed tasks are always viewable, but not editable
  const isCompleted = task.completed;

  // Determine task relationship to current user for visual badges
  const taskRelation = getTaskRelationship(task, user?.id);

  // Attachments preview for the card
  const attachments = task.attachments || [];
  const attachmentCount = task._count?.attachments ?? attachments.length;

  // Fetch attachments when expanding if the list endpoint didn't include them
  const [cardAttachments, setCardAttachments] = useState<Attachment[]>(attachments);
  useEffect(() => {
    setCardAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    if (!isExpanded) return;
    if (attachments.length > 0) return;
    if (!task.id || (task._count?.attachments ?? 0) === 0) return;

    let cancelled = false;
    attachmentApi.list(task.id).then((response) => {
      if (!cancelled && response.success && response.data) {
        setCardAttachments(response.data as Attachment[]);
      }
    }).catch(() => {
      // silently ignore — list already returned empty attachments
    });

    return () => { cancelled = true; };
  }, [isExpanded, attachments.length, task.id, task._count?.attachments]);

  const displayAttachments = cardAttachments.length > 0 ? cardAttachments : attachments;
  const visibleAttachments = displayAttachments.slice(0, 3);
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  const showCreatedBadge = taskRelation.isCreatedByMe && !taskRelation.isAssignedToMe;
  const showAssignedBadge = taskRelation.isAssignedToMe && !taskRelation.isCreatedByMe;
  const showBothBadges = taskRelation.isCreatedByMe && taskRelation.isAssignedToMe;

  // Measure expanded content height for smooth animation
  // Uses ResizeObserver to detect dynamic height changes (e.g., group copies dropdown loading/expanding)
  useEffect(() => {
    if (!contentRef.current || !isExpanded) {
      setContentHeight(0);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.target.scrollHeight);
      }
    });

    observer.observe(contentRef.current);
    setContentHeight(contentRef.current.scrollHeight);

    return () => observer.disconnect();
  }, [isExpanded]);

  const handleCardClick = () => {
    // Toggle expansion - only one card can be expanded at a time
    toggleExpand(task.id);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open edit modal only if user has permission
    if (canEdit) {
      openTaskModal(task.id);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'text-red-500';
      case 'HIGH':
        return 'text-orange-500';
      case 'MEDIUM':
        return 'text-blue-500';
      default:
        return 'text-gray-400';
    }
  };

  const getPriorityBgColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'HIGH':
        return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
      case 'MEDIUM':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      default:
        return 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700';
    }
  };

  const getPriorityBorderColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'border-l-red-500 dark:border-l-red-400';
      case 'HIGH': return 'border-l-orange-500 dark:border-l-orange-400';
      case 'MEDIUM': return 'border-l-blue-500 dark:border-l-blue-400';
      default: return 'border-l-gray-400 dark:border-l-gray-500';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return t('tasks.card.priority.urgent');
      case 'HIGH':
        return t('tasks.card.priority.high');
      case 'MEDIUM':
        return t('tasks.card.priority.medium');
      default:
        return t('tasks.card.priority.low');
    }
  };

  // Validate date string - check for null, undefined, empty string, or invalid date
  const isValidDateString = (dateStr: string | null | undefined): boolean => {
    if (!dateStr || dateStr === '' || dateStr === null || dateStr === undefined) {
      return false;
    }
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  };

  const getRecurringLabel = () => {
    if (!task.recurringRule) return null;

    const rule = task.recurringRule;
    let label = '';

    switch (rule.frequency) {
      case 'DAILY':
        label = t('tasks.card.recurring.daily', { interval: rule.interval });
        break;
      case 'WEEKLY':
        label = t('tasks.card.recurring.weekly', { interval: rule.interval });
        break;
      case 'MONTHLY':
        label = t('tasks.card.recurring.monthly', { interval: rule.interval });
        break;
    }

    if (rule.isPaused) {
      label += ' ' + t('tasks.recurring.paused');
    }

    return label;
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await deleteTask(task.id);
    setIsDeleting(false);
    setShowDeleteModal(false);
    // Collapse if this task was expanded
    if (isExpanded) {
      toggleExpand(task.id);
    }
  };

  const dueDateObj = isValidDateString(task.dueDate) ? new Date(task.dueDate as string) : null;
  const nextDueDateObj = isValidDateString(task.nextDueDate) ? new Date(task.nextDueDate as string) : null;
  const isOverdue = dueDateObj !== null && 
                    isPast(dueDateObj) && 
                    !isToday(dueDateObj);
  const isNextOverdue = nextDueDateObj !== null && 
                       isPast(nextDueDateObj) && 
                       !isToday(nextDueDateObj);
  const recurringLabel = getRecurringLabel();
  
  // RECURRING FIX: Determine if this is a template (recurringParentId === null) or an instance
  // Templates have nextDueDate, instances have dueDate
  const isRecurringTemplate = task.recurringRule && !task.recurringParentId;
  // const isRecurringInstance = task.recurringRule && task.recurringParentId; // For future use

  const isModified = task.updatedAt && task.createdAt
    ? new Date(task.updatedAt).getTime() - new Date(task.createdAt).getTime() > 2000
    : false;

  return (
    <>
      <div
        className={`
          group rounded-lg border border-l-4 transition-all duration-200 cursor-pointer ${isExpanded ? 'overflow-visible' : 'overflow-hidden'}
          ${isExpanded 
            ? 'shadow-lg border-primary-300 dark:border-primary-700' 
            : 'sm:hover:shadow-md'
          }
          ${task.completed 
            ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 sm:hover:border-gray-300 sm:dark:hover:border-gray-600' 
            : isExpanded 
              ? 'bg-white dark:bg-gray-900 border-primary-300 dark:border-primary-700' 
              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 sm:hover:border-primary-300 sm:dark:hover:border-primary-700'
          }
          ${getPriorityBorderColor(task.priority)}
        `}
      >
        {/* Main clickable header area */}
        <div 
          className="p-3 sm:p-4"
          onClick={handleCardClick}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={t('tasks.card.ariaLabel', { title: task.title, state: isExpanded ? t('tasks.card.expanded') : t('tasks.card.collapsed') })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
        >
          <div className="flex items-start gap-3">
            {/* Checkbox - separate click handler */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleComplete(task.id);
              }}
              className="mt-0.5 flex-shrink-0 p-1 -m-1 min-h-[44px] min-w-[44px] flex items-center justify-center sm:min-h-0 sm:min-w-0 sm:p-0 sm:m-0"
            >
              {task.completed ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <Circle className="w-5 h-5 text-gray-400 hover:text-primary-500 transition-colors" />
              )}
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Title row with icons and expand indicator - responsive: title full-width on mobile, inline on desktop */}
              <div className="min-w-0">
                <div className="flex items-start gap-1.5 min-w-0">
                  <span
                    title={task.title}
                    className={`
                      text-sm font-medium min-w-0 flex-1
                      ${isExpanded ? '' : 'line-clamp-3 sm:truncate'}
                      ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-gray-100'}
                    `}
                  >
                    {task.title}
                  </span>
                  {isModified && (
                    <span 
                      className="text-[10px] text-gray-400 dark:text-gray-500 italic font-normal hidden sm:inline-flex flex-shrink-0"
                      title={t('tasks.card.edited', { date: format(new Date(task.updatedAt), 'MMM d, HH:mm', { locale: dateFnsLocale }) })}
                    >
                      {t('tasks.card.edited', { date: format(new Date(task.updatedAt), 'MMM d, HH:mm', { locale: dateFnsLocale }) })}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 flex-shrink-0 sm:ml-auto">
                    {/* Private indicator */}
                    {task.isPrivate && (
                      <Lock className="w-3.5 h-3.5 text-amber-500" />
                    )}
                    {/* Completed indicator */}
                    {isCompleted && (
                      <span title={t('tasks.card.viewOnlyCompleted')}>
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                      </span>
                    )}
                    {/* Expand/collapse indicator */}
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-primary-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
                    )}
                  </div>
                </div>
                  {/* Mobile-only edited label - appears below title on small screens */}
                  {isModified && (
                    <span
                      className="sm:hidden text-[10px] text-gray-400 dark:text-gray-500 italic font-normal mt-0.5"
                      title={t('tasks.card.edited', { date: format(new Date(task.updatedAt), 'MMM d, HH:mm', { locale: dateFnsLocale }) })}
                    >
                      {t('tasks.card.edited', { date: format(new Date(task.updatedAt), 'MMM d, HH:mm', { locale: dateFnsLocale }) })}
                    </span>
                  )}
                {/* Recurring indicator - visible on all screens */}
                {task.recurringRule && (
                  <span className={`flex items-center gap-1 text-xs flex-shrink-0 ${task.recurringRule.isPaused ? 'text-amber-500' : 'text-primary-500'}`}>
                    <Repeat className="w-3 h-3" />
                    <span>{recurringLabel}</span>
                  </span>
                )}
              </div>

              {/* Meta info - compact preview (visible when collapsed) */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-3 mt-1.5 text-xs text-gray-500">
                {/* Due date - PROMINENT POSITION for quick reference */}
                {/* RECURRING FIX: Templates show nextDueDate as "Next occurrence", instances show dueDate */}
                {isRecurringTemplate && nextDueDateObj ? (
                  <span
                    className={`
                      inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0 text-xs font-medium
                      ${isNextOverdue 
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                        : isToday(nextDueDateObj)
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                          : isTomorrow(nextDueDateObj)
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                      }
                    `}
                    title={t('tasks.card.nextOccurrenceFull', { date: format(nextDueDateObj, 'EEEE, MMMM d, yyyy', { locale: dateFnsLocale }) })}
                  >
                    <Repeat className="w-3 h-3" />
                    {t('tasks.card.nextPrefix')} {isToday(nextDueDateObj) ? t('dates.today') : isTomorrow(nextDueDateObj) ? t('dates.tomorrow') : format(nextDueDateObj, 'MMM d', { locale: dateFnsLocale })}
                    {isNextOverdue && ` (${t('tasks.card.overdue')})`}
                  </span>
                ) : dueDateObj ? (
                  <span
                    className={`
                      inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0 text-xs font-medium
                      ${isOverdue 
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                        : isToday(dueDateObj)
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                          : isTomorrow(dueDateObj)
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }
                    `}
                    title={t('tasks.card.due', { date: format(dueDateObj, 'EEEE, MMMM d, yyyy', { locale: dateFnsLocale }) })}
                  >
                    <Clock className="w-3 h-3" />
                    {isToday(dueDateObj) ? t('dates.today') : isTomorrow(dueDateObj) ? t('dates.tomorrow') : format(dueDateObj, 'MMM d', { locale: dateFnsLocale })}
                    {isOverdue && ` (${t('tasks.card.overdue')})`}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0 text-xs bg-gray-100 dark:bg-gray-800 text-gray-400">
                    <Clock className="w-3 h-3" />
                    {t('tasks.card.noDueDate')}
                  </span>
                )}
                
                {task.project && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: `${task.project.color}20`, color: task.project.color }}
                  >
                    {task.project.name}
                  </span>
                )}
                
                {/* Ownership badges */}
                {showCreatedBadge && (
                  <span 
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                    title={t('tasks.card.createdByYou')}
                  >
                    <Crown className="w-3 h-3" />
                    {t('tasks.card.createdByYou')}
                  </span>
                )}
                
                {showAssignedBadge && (
                  <span 
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                    title={t('tasks.card.assignedToYou')}
                  >
                    <UserCheck className="w-3 h-3" />
                    {t('tasks.card.assignedToYou')}
                  </span>
                )}
                
                {showBothBadges && (
                  <span 
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
                    title={t('tasks.card.createdAndAssignedToYou')}
                  >
                    <Crown className="w-3 h-3" />
                    <UserCheck className="w-3 h-3" />
                    {t('tasks.card.createdAndAssignedToYou')}
                  </span>
                )}

                {/* Claim unassigned task button */}
                {canClaimTaskResult && (
                  <button
                    data-testid="claim-task-button"
                    onClick={handleAssignToMe}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                  >
                    <UserPlus className="w-3 h-3" />
                    {t('tasks.card.assignToMe')}
                  </button>
                )}

                {/* Individual task copies indicator — collapsed view */}
                {isCreatorAndHasGroup && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
                    title={t('tasks.card.teamCopiesTitle')}
                  >
                    <Users className="w-3 h-3" />
                    {t('tasks.card.teamCopies')}
                  </span>
                )}
                {/* Progress indicator — collapsed view (only if copies are cached) */}
                {isCreatorAndHasGroup && groupCopies[task.id] && groupCopies[task.id].length > 0 && (
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {groupCopies[task.id].filter(c => c.completed).length}/{groupCopies[task.id].length} {t('tasks.card.completed')}
                  </span>
                )}

                {/* Attachments count */}
                {task._count?.attachments ? (
                  <span className="text-gray-400 flex-shrink-0">
                    📎 {task._count.attachments}
                  </span>
                ) : null}

                {/* Subtasks progress */}
                {task.subtasks && task.subtasks.length > 0 && (
                  <span className="text-gray-400 flex-shrink-0">
                    ☑️ {task.subtasks.filter(s => s.completed).length}/{task.subtasks.length}
                  </span>
                )}
                
                {/* Relative creation time */}
                {createdAgo && (
                  <span 
                    className="inline-flex items-center gap-1 text-gray-400 flex-shrink-0"
                    title={t('tasks.card.created', { date: format(new Date(task.createdAt), 'PPP p', { locale: dateFnsLocale }) })}
                  >
                    <Calendar className="w-3 h-3" />
                    {createdAgo}
                  </span>
                )}
              </div>

              {/* Labels (collapsed view) */}
              {task.labels.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {task.labels.slice(0, 3).map((label) => (
                    <span
                      key={label}
                      className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    >
                      {label}
                    </span>
                  ))}
                  {task.labels.length > 3 && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                      +{task.labels.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Attachment thumbnails (collapsed view) */}
              {visibleAttachments.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex -space-x-2">
                    {visibleAttachments.map((attachment, index) => (
                      <button
                        key={attachment.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openLightbox(index); }}
                        className="relative w-8 h-8 rounded-md overflow-hidden border border-white dark:border-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        aria-label={t('tasks.card.openImage', { index: index + 1 })}
                        title={attachment.fileName}
                      >
                        <img
                          src={attachment.url}
                          alt={attachment.fileName}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  {attachmentCount > visibleAttachments.length && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" />
                      +{attachmentCount - visibleAttachments.length}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Action buttons - Edit (pencil) and Delete (trash) */}
            {/* Always rendered to maintain vertical alignment across rows */}
            {/* Buttons use invisible+pointer-events-none when no permission, preserving layout space */}
            <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                onClick={handleEditClick}
                className={`p-2 sm:p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-500 transition-colors min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center ${!canEdit ? 'invisible pointer-events-none' : ''}`}
                title={canEdit ? t('tasks.card.editTask') : ''}
                tabIndex={canEdit ? 0 : -1}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                }}
                className={`p-2 sm:p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center ${!canDelete ? 'invisible pointer-events-none' : ''}`}
                title={canDelete ? t('tasks.card.deleteTask') : ''}
                tabIndex={canDelete ? 0 : -1}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Expandable content section with smooth animation */}
        <div 
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: isExpanded ? contentHeight : 0 }}
        >
          <div 
            ref={contentRef}
            className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-800"
          >
            {/* Full task details grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Description */}
              {task.description && (
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <FileText className="w-3.5 h-3.5" />
                    <span className="font-medium">{t('tasks.card.description')}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 rounded p-2 whitespace-pre-wrap">
                    {task.description}
                  </p>
                </div>
              )}

              {/* Notes */}
              {task.notes && (
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <StickyNote className="w-3.5 h-3.5" />
                    <span className="font-medium">{t('tasks.card.notes')}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-amber-50 dark:bg-amber-900/20 rounded p-2 whitespace-pre-wrap border border-amber-100 dark:border-amber-800/30">
                    {task.notes}
                  </p>
                </div>
              )}

              {/* Attachments gallery — expanded view */}
              {displayAttachments.length > 0 && (
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <ImageIcon className="w-3.5 h-3.5" />
                    <span className="font-medium">{t('tasks.card.images')}</span>
                    {attachmentCount > displayAttachments.length && (
                      <span className="text-xs text-gray-400">({attachmentCount} {t('tasks.card.total')})</span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {displayAttachments.map((attachment, index) => (
                      <button
                        key={attachment.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openLightbox(index); }}
                        className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 text-left"
                        aria-label={t('tasks.card.openImageNamed', { index: index + 1, fileName: attachment.fileName })}
                        title={attachment.fileName}
                      >
                        <img
                          src={attachment.url}
                          alt={attachment.fileName}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                        <span className="absolute bottom-0 left-0 right-0 px-1.5 py-1 text-[10px] text-white bg-black/60 truncate">
                          {attachment.fileName}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Priority */}
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <span className="font-medium">{t('tasks.card.priorityLabel')}</span>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${getPriorityBgColor(task.priority)} ${getPriorityColor(task.priority)}`}>
                  {getPriorityLabel(task.priority)}
                </span>
              </div>

              {/* Due Date */}
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="font-medium">{isRecurringTemplate ? t('tasks.card.nextOccurrenceLabel') : t('tasks.card.dueDateLabel')}</span>
                </div>
                {/* RECURRING FIX: Templates show nextDueDate, instances show dueDate */}
                {isRecurringTemplate && nextDueDateObj ? (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isNextOverdue ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800' : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'}`}>
                    <Repeat className="w-3 h-3" />
                    {format(nextDueDateObj, 'EEEE, MMMM d, yyyy', { locale: dateFnsLocale })}
                    {isNextOverdue && <span className="font-medium">({t('tasks.card.overdueLabel')})</span>}
                  </span>
                ) : task.dueDate && isValidDateString(task.dueDate) ? (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isOverdue ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300'}`}>
                    {format(new Date(task.dueDate), 'EEEE, MMMM d, yyyy', { locale: dateFnsLocale })}
                    {isOverdue && <span className="font-medium">({t('tasks.card.overdueLabel')})</span>}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">{t('tasks.card.noDueDate')}</span>
                )}
              </div>

              {/* Project */}
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <FolderOpen className="w-3.5 h-3.5" />
                  <span className="font-medium">{t('tasks.card.projectLabel')}</span>
                </div>
                {task.project ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                    style={{ backgroundColor: `${task.project.color}20`, color: task.project.color }}
                  >
                    {task.project.name}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">{t('tasks.card.noProject')}</span>
                )}
              </div>

              {/* Reminder */}
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Bell className="w-3.5 h-3.5" />
                  <span className="font-medium">{t('tasks.card.reminderLabel')}</span>
                </div>
                {task.remindAt && isValidDateString(task.remindAt) ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400">
                    {format(new Date(task.remindAt), 'MMM d, HH:mm', { locale: dateFnsLocale })}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">{t('tasks.card.noReminder')}</span>
                )}
              </div>

              {/* Creator */}
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Users className="w-3.5 h-3.5" />
                  <span className="font-medium">{t('tasks.card.creatorLabel')}</span>
                </div>
                {task.creator ? (
                  <div className="flex items-center gap-2">
                    {task.creator.avatar ? (
                      <img src={task.creator.avatar} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs">
                        {task.creator.firstName[0]}
                      </div>
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {task.creator.firstName} {task.creator.lastName}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">-</span>
                )}
              </div>

              {/* Assignee */}
              <div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <UserCheck className="w-3.5 h-3.5" />
                  <span className="font-medium">{t('tasks.card.assigneeLabel')}</span>
                </div>
                {task.assignee ? (
                  <div className="flex items-center gap-2">
                    {task.assignee.avatar ? (
                      <img src={task.assignee.avatar} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs">
                        {task.assignee.firstName[0]}
                      </div>
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {task.assignee.firstName} {task.assignee.lastName}
                    </span>
                  </div>
                ) : canClaimTaskResult ? (
                  <button
                    data-testid="claim-task-button"
                    onClick={handleAssignToMe}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                  >
                    <UserPlus className="w-3 h-3" />
                    {t('tasks.card.assignToMe')}
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">{t('tasks.card.unassigned')}</span>
                )}
              </div>

              {/* Completed By */}
              {task.completed && task.completedBy && (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    <span className="font-medium">{t('tasks.card.completedByLabel')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {task.completedBy.avatar ? (
                      <img src={task.completedBy.avatar} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-xs text-green-600">
                        {task.completedBy.firstName[0]}
                      </div>
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {task.completedBy.firstName} {task.completedBy.lastName}
                    </span>
                  </div>
                </div>
              )}

              {/* Completed At */}
              {task.completed && task.completedAt && (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <Calendar className="w-3.5 h-3.5 text-green-500" />
                    <span className="font-medium">{t('tasks.card.completedAtLabel')}</span>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {format(new Date(task.completedAt), 'MMM d, yyyy, HH:mm', { locale: dateFnsLocale })}
                  </span>
                </div>
              )}

              {/* Recurring Rule */}
              {task.recurringRule && (
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <Repeat className="w-3.5 h-3.5" />
                    <span className="font-medium">{t('tasks.card.recurringLabel')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${task.recurringRule.isPaused ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600' : 'bg-primary-50 dark:bg-primary-900/20 text-primary-600'}`}>
                      {getRecurringLabel()}
                    </span>
                    {task.recurringRule.endDate && (
                      <span className="text-xs text-gray-500">
                        {t('tasks.recurring.until', { date: format(new Date(task.recurringRule.endDate), 'MMM d, yyyy', { locale: dateFnsLocale }) })}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* All Labels */}
            {task.labels.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                  <Tag className="w-3.5 h-3.5" />
                  <span className="font-medium">{t('tasks.card.labelsLabel')}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {task.labels.map((label) => (
                    <span
                      key={label}
                      className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Subtasks */}
            {task.subtasks && task.subtasks.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <span className="font-medium">{t('tasks.card.subtasksLabel')} ({task.subtasks.filter(s => s.completed).length}/{task.subtasks.length})</span>
                </div>
                <div className="space-y-1">
                  {task.subtasks.map((subtask) => (
                    <div 
                      key={subtask.id}
                      className={`flex items-center gap-2 text-sm ${subtask.completed ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}
                    >
                      {subtask.completed ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-400" />
                      )}
                      <span className={subtask.completed ? 'line-through' : ''}>
                        {subtask.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Task Group Copies Dropdown — only visible for the creator of an Individual task with copies */}
            {isCreatorAndHasGroup && (
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                <TaskGroupCopiesDropdown
                  taskId={task.id}
                  creatorId={task.creatorId}
                />
              </div>
            )}

            {/* Creation timestamp */}
            <div className="mt-4 pt-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {t('tasks.card.created', { date: format(new Date(task.createdAt), 'MMM d, yyyy, HH:mm', { locale: dateFnsLocale }) })}
            </div>
          </div>
        </div>
      </div>

      <ImageLightbox
        images={displayAttachments.map((a) => a.url)}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('tasks.card.deleteTask')}
        size="sm"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('tasks.card.deleteConfirmTitle')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('tasks.card.deleteConfirmMessage', { title: task.title })}
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete}
              isLoading={isDeleting}
              className="w-full sm:w-auto"
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TaskCard;